/**
 * Election-system evolution engine.
 *
 * Framework-agnostic simulation: agent-based voters on a 2-D opinion space,
 * adaptive parties that chase votes, and four tallying rules (FPTP, IRV,
 * proportional/D'Hondt, approval). Renders directly to three <canvas>
 * elements via requestAnimationFrame so the hot loop never routes through
 * React's render cycle (per CLAUDE.md rule #4). React owns only the UI shell
 * and pushes config changes in through `setConfig`; the engine reports
 * cycle-boundary state back through the `onStats` callback.
 */

const DEFAULT_CONFIG = {
  system: 'fptp',
  voters: 1500,
  startParties: 7,
  strategic: 0.65, // 0..1 fraction of voters willing to vote strategically
  dist: 'one',
  mobility: 0.35, // how fast parties drift toward their base
  magnitude: 5, // seats per district (proportional only)
  deathThreshold: 0.06, // vote-share floor
  deathPatience: 4, // cycles below floor before a party dies
  entrants: false,
  framesPerCycle: 10,
  viewMode: 'ecosystem', // 'ecosystem' (creatures eating food) | 'analyst' (abstract dots)
};

const HISTORY_MAX = 220;
const isSingleWinner = (s) => s === 'fptp' || s === 'irv' || s === 'approval';

const rand = () => Math.random();
const clamp01 = (v) => Math.max(0.02, Math.min(0.98, v));

function gaussian(mean, sd) {
  let u = 0;
  let v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return mean + sd * Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

function partyColor(i) {
  const hue = (i * 47) % 360;
  return `hsl(${hue} 72% 62%)`;
}

export function createElectionSim({ spectrum, enpChart, shareChart, onStats, onRunningChange }) {
  const sctx = spectrum.getContext('2d');
  const ectx = enpChart.getContext('2d');
  const cctx = shareChart.getContext('2d');

  const cfg = { ...DEFAULT_CONFIG };

  let voters = [];
  let parties = [];
  let nextPartyId = 0;
  let cycle = 0;
  let history = [];
  let running = false;
  let frame = 0;
  let rafId = null;

  // ----------------------------------------------------------- construction
  function makeParty(x, y) {
    const id = nextPartyId++;
    return {
      id,
      x,
      y,
      px: x,
      py: y,
      dx: x,
      dy: y,
      color: partyColor(id),
      share: 0,
      seats: 0,
      alive: true,
      fade: 1,
      lowStreak: 0,
    };
  }

  const DIST_CENTERS = {
    one: [[0.5, 0.5]],
    two: [
      [0.32, 0.42],
      [0.7, 0.62],
    ],
    three: [
      [0.3, 0.35],
      [0.72, 0.4],
      [0.5, 0.75],
    ],
  };

  function seedVoters() {
    voters = [];
    const centers = DIST_CENTERS[cfg.dist] ?? null; // null → uniform spread
    for (let i = 0; i < cfg.voters; i++) {
      let x;
      let y;
      if (!centers) {
        x = rand();
        y = rand();
      } else {
        const c = centers[(Math.random() * centers.length) | 0];
        x = clamp01(gaussian(c[0], 0.13));
        y = clamp01(gaussian(c[1], 0.13));
      }
      voters.push({ x, y, party: -1 });
    }
  }

  function seedParties() {
    parties = [];
    nextPartyId = 0;
    for (let i = 0; i < cfg.startParties; i++) {
      parties.push(makeParty(clamp01(rand()), clamp01(rand())));
    }
  }

  // -------------------------------------------------------------- elections
  const alive = () => parties.filter((p) => p.alive);
  const dist2 = (a, bx, by) => (a.x - bx) * (a.x - bx) + (a.y - by) * (a.y - by);

  function viableSet() {
    const live = alive();
    if (live.length === 0) return new Set();
    const withPrev = live.filter((p) => p.share > 0);
    const ranked = [...(withPrev.length ? withPrev : live)].sort((a, b) => b.share - a.share);
    const set = new Set();
    ranked.slice(0, 2).forEach((p) => set.add(p.id));
    ranked.forEach((p) => {
      if (p.share >= cfg.deathThreshold) set.add(p.id);
    });
    return set;
  }

  function rankByProximity(v, live) {
    return [...live].sort((a, b) => dist2(a, v.x, v.y) - dist2(b, v.x, v.y));
  }

  function allocateDhondt(live, votes, seats) {
    for (let s = 0; s < seats; s++) {
      let best = null;
      let bestQ = -1;
      for (const p of live) {
        const q = votes.get(p.id) / (p.seats + 1);
        if (q > bestQ) {
          bestQ = q;
          best = p;
        }
      }
      if (best) best.seats++;
    }
  }

  function runIRV(ballots, live) {
    const eliminated = new Set();
    let liveCount = live.length;
    for (;;) {
      const tally = new Map();
      live.forEach((p) => {
        if (!eliminated.has(p.id)) tally.set(p.id, 0);
      });
      for (const b of ballots) {
        const top = b.find((id) => !eliminated.has(id));
        if (top !== undefined) tally.set(top, (tally.get(top) || 0) + 1);
      }
      const totalValid = [...tally.values()].reduce((a, c) => a + c, 0);
      let leader = null;
      let leadV = -1;
      let loser = null;
      let loseV = Infinity;
      for (const [id, v] of tally) {
        if (v > leadV) {
          leadV = v;
          leader = id;
        }
        if (v < loseV) {
          loseV = v;
          loser = id;
        }
      }
      if (leader !== null && leadV > totalValid / 2) return { winner: leader };
      if (tally.size <= 2) return { winner: leader };
      eliminated.add(loser);
      liveCount -= 1;
      if (liveCount <= 2) {
        const t2 = new Map();
        for (const b of ballots) {
          const top = b.find((id) => !eliminated.has(id));
          if (top !== undefined) t2.set(top, (t2.get(top) || 0) + 1);
        }
        let w = null;
        let wv = -1;
        for (const [id, v] of t2) {
          if (v > wv) {
            wv = v;
            w = id;
          }
        }
        return { winner: w };
      }
    }
  }

  function computeENP(shares) {
    let sum = 0;
    for (const s of shares.values()) sum += s * s;
    return sum > 0 ? 1 / sum : 0;
  }

  function recordHistory(shares) {
    history.push({ enp: computeENP(shares), shares: new Map(shares) });
    if (history.length > HISTORY_MAX) history.shift();
  }

  function runElection() {
    const live = alive();
    if (live.length === 0) {
      recordHistory(new Map());
      return;
    }

    const viable = viableSet();
    const first = new Map();
    live.forEach((p) => first.set(p.id, 0));
    const approvals = new Map();
    live.forEach((p) => approvals.set(p.id, 0));
    const approvalR2 = 0.16 * 0.16;
    const ballots = cfg.system === 'irv' ? [] : null;

    for (const v of voters) {
      const ranked = rankByProximity(v, live);
      let choice = ranked[0];

      if (cfg.system === 'approval') {
        let approvedAny = false;
        for (const p of ranked) {
          if (dist2(p, v.x, v.y) <= approvalR2) {
            approvals.set(p.id, approvals.get(p.id) + 1);
            approvedAny = true;
          }
        }
        if (Math.random() < cfg.strategic) {
          const bestViable = ranked.find((p) => viable.has(p.id));
          if (bestViable && dist2(bestViable, v.x, v.y) > approvalR2) {
            approvals.set(bestViable.id, approvals.get(bestViable.id) + 1);
          }
        }
        if (!approvedAny) approvals.set(choice.id, approvals.get(choice.id) + 1);
        first.set(choice.id, first.get(choice.id) + 1);
        v.party = choice.id;
        continue;
      }

      if (cfg.system === 'fptp') {
        if (Math.random() < cfg.strategic && !viable.has(choice.id) && viable.size > 0) {
          const alt = ranked.find((p) => viable.has(p.id));
          if (alt) choice = alt;
        }
      }
      first.set(choice.id, first.get(choice.id) + 1);
      v.party = choice.id;
      if (ballots) ballots.push(ranked.map((p) => p.id));
    }

    const total = voters.length;
    const shares = new Map();
    live.forEach((p) => (p.seats = 0));

    if (cfg.system === 'approval') {
      const totApprove = [...approvals.values()].reduce((a, b) => a + b, 0) || 1;
      live.forEach((p) => shares.set(p.id, approvals.get(p.id) / totApprove));
      [...live]
        .sort((a, b) => approvals.get(b.id) - approvals.get(a.id))
        .slice(0, 1)
        .forEach((p) => (p.seats = 1));
      live.forEach((p) => (p.share = first.get(p.id) / total));
    } else if (cfg.system === 'pr') {
      live.forEach((p) => shares.set(p.id, first.get(p.id) / total));
      allocateDhondt(live, first, cfg.magnitude);
      live.forEach((p) => (p.share = shares.get(p.id)));
    } else if (cfg.system === 'irv') {
      const res = runIRV(ballots, live);
      live.forEach((p) => shares.set(p.id, first.get(p.id) / total));
      if (res.winner !== null && res.winner !== undefined) {
        const w = live.find((p) => p.id === res.winner);
        if (w) w.seats = 1;
      }
      live.forEach((p) => (p.share = first.get(p.id) / total));
    } else {
      live.forEach((p) => shares.set(p.id, first.get(p.id) / total));
      let win = live[0];
      live.forEach((p) => {
        if (first.get(p.id) > first.get(win.id)) win = p;
      });
      win.seats = 1;
      live.forEach((p) => (p.share = first.get(p.id) / total));
    }

    recordHistory(shares);
  }

  // ---------------------------------------------------- evolve, death, birth
  function computeTargets() {
    const live = alive();
    const sumX = new Map();
    const sumY = new Map();
    const cnt = new Map();
    live.forEach((p) => {
      sumX.set(p.id, 0);
      sumY.set(p.id, 0);
      cnt.set(p.id, 0);
    });
    for (const v of voters) {
      if (sumX.has(v.party)) {
        sumX.set(v.party, sumX.get(v.party) + v.x);
        sumY.set(v.party, sumY.get(v.party) + v.y);
        cnt.set(v.party, cnt.get(v.party) + 1);
      }
    }

    for (const p of live) {
      p.px = p.dx;
      p.py = p.dy;
      const n = cnt.get(p.id);
      if (n > 0) {
        const cx = sumX.get(p.id) / n;
        const cy = sumY.get(p.id) / n;
        p.x = clamp01(p.x + cfg.mobility * (cx - p.x) + gaussian(0, 0.006));
        p.y = clamp01(p.y + cfg.mobility * (cy - p.y) + gaussian(0, 0.006));
      } else {
        p.x = clamp01(p.x + gaussian(0, 0.03));
        p.y = clamp01(p.y + gaussian(0, 0.03));
      }

      if (p.share < cfg.deathThreshold) p.lowStreak++;
      else p.lowStreak = 0;
      if (p.lowStreak >= cfg.deathPatience && alive().length > 1) p.alive = false;
    }

    if (cfg.entrants && alive().length < 10 && Math.random() < 0.25) {
      let best = null;
      let bestD = -1;
      for (let k = 0; k < 40; k++) {
        const v = voters[(Math.random() * voters.length) | 0];
        let nearest = Infinity;
        for (const p of alive()) nearest = Math.min(nearest, dist2(p, v.x, v.y));
        if (nearest > bestD) {
          bestD = nearest;
          best = v;
        }
      }
      if (best) parties.push(makeParty(best.x, best.y));
    }
  }

  // ------------------------------------------------------------- rendering
  function fitCanvas(cv, ctx) {
    const dpr = window.devicePixelRatio || 1;
    const r = cv.getBoundingClientRect();
    cv.width = r.width * dpr;
    cv.height = r.height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { w: r.width, h: r.height };
  }

  // Draws one googly-eyed "creature" (a party) that eats voter-food. Size grows
  // with vote share; eyes track the direction it's drifting (hunting); a dying
  // creature gets X_X eyes; seat-winners wear a crown.
  function drawCreature(cx, cy, r, p, ease) {
    const bodyR = Math.max(11, r * 0.72);
    // hunting direction from this cycle's drift (dx already tweened)
    let vx = p.x - p.px;
    let vy = p.y - p.py;
    const mag = Math.hypot(vx, vy) || 1;
    vx /= mag;
    vy /= mag;

    // soft glow
    const glow = sctx.createRadialGradient(cx, cy, bodyR * 0.4, cx, cy, bodyR * 1.7);
    glow.addColorStop(0, p.color);
    glow.addColorStop(1, 'transparent');
    sctx.globalAlpha = p.fade * 0.35;
    sctx.fillStyle = glow;
    sctx.beginPath();
    sctx.arc(cx, cy, bodyR * 1.7, 0, Math.PI * 2);
    sctx.fill();
    sctx.globalAlpha = p.fade;

    // body
    const body = sctx.createRadialGradient(cx - bodyR * 0.3, cy - bodyR * 0.4, 2, cx, cy, bodyR);
    body.addColorStop(0, '#ffffff55');
    body.addColorStop(0.25, p.color);
    body.addColorStop(1, p.color);
    sctx.fillStyle = body;
    sctx.beginPath();
    sctx.arc(cx, cy, bodyR, 0, Math.PI * 2);
    sctx.fill();
    sctx.strokeStyle = 'rgba(0,0,0,.35)';
    sctx.lineWidth = 2;
    sctx.stroke();

    // eyes
    const eyeR = Math.max(3.5, bodyR * 0.3);
    const eyeDX = bodyR * 0.38;
    const eyeDY = -bodyR * 0.18;
    const dead = !p.alive;
    for (const sign of [-1, 1]) {
      const ex = cx + sign * eyeDX;
      const ey = cy + eyeDY;
      if (dead) {
        // X_X
        sctx.strokeStyle = '#1a1a1a';
        sctx.lineWidth = 2;
        const s = eyeR * 0.7;
        sctx.beginPath();
        sctx.moveTo(ex - s, ey - s);
        sctx.lineTo(ex + s, ey + s);
        sctx.moveTo(ex + s, ey - s);
        sctx.lineTo(ex - s, ey + s);
        sctx.stroke();
      } else {
        sctx.fillStyle = '#fff';
        sctx.beginPath();
        sctx.arc(ex, ey, eyeR, 0, Math.PI * 2);
        sctx.fill();
        // pupil looks toward hunting direction
        sctx.fillStyle = '#101018';
        sctx.beginPath();
        sctx.arc(ex + vx * eyeR * 0.45, ey + vy * eyeR * 0.45, eyeR * 0.52, 0, Math.PI * 2);
        sctx.fill();
      }
    }

    // mouth — happier (open, chomping) when winning a seat
    sctx.strokeStyle = '#1a1a1a';
    sctx.lineWidth = 2;
    const my = cy + bodyR * 0.34;
    if (!dead && p.seats > 0) {
      // open chomp mouth that pulses with the cycle
      const chomp = 0.5 + 0.5 * Math.abs(Math.sin(ease * Math.PI));
      sctx.fillStyle = '#3a1720';
      sctx.beginPath();
      sctx.ellipse(cx, my, bodyR * 0.34, bodyR * 0.22 * chomp + 1, 0, 0, Math.PI * 2);
      sctx.fill();
      sctx.stroke();
    } else {
      sctx.beginPath();
      const dip = dead ? -bodyR * 0.16 : bodyR * 0.16; // frown if dead
      sctx.moveTo(cx - bodyR * 0.28, my);
      sctx.quadraticCurveTo(cx, my + dip, cx + bodyR * 0.28, my);
      sctx.stroke();
    }

    // crown for seat winners
    if (p.seats > 0 && !dead) {
      sctx.fillStyle = '#ffd257';
      const cw = bodyR * 0.7;
      const cyTop = cy - bodyR - 5;
      sctx.beginPath();
      sctx.moveTo(cx - cw, cyTop + 8);
      sctx.lineTo(cx - cw, cyTop);
      sctx.lineTo(cx - cw * 0.5, cyTop + 5);
      sctx.lineTo(cx, cyTop - 3);
      sctx.lineTo(cx + cw * 0.5, cyTop + 5);
      sctx.lineTo(cx + cw, cyTop);
      sctx.lineTo(cx + cw, cyTop + 8);
      sctx.closePath();
      sctx.fill();
      if (p.seats > 1) {
        sctx.fillStyle = '#1a1a1a';
        sctx.font = '700 9px sans-serif';
        sctx.textAlign = 'center';
        sctx.fillText(String(p.seats), cx, cyTop + 7);
      }
    }

    // name
    sctx.fillStyle = dead ? '#6b7897' : '#e7ecf5';
    sctx.font = '700 11px sans-serif';
    sctx.textAlign = 'center';
    sctx.fillText(`P${p.id}`, cx, cy + bodyR + 14);
  }

  // Abstract "analyst" rendering: party as a translucent blob with a core.
  function drawBlob(cx, cy, r, p) {
    const g = sctx.createRadialGradient(cx, cy, 2, cx, cy, r);
    g.addColorStop(0, p.color);
    g.addColorStop(1, 'transparent');
    sctx.fillStyle = g;
    sctx.beginPath();
    sctx.arc(cx, cy, r, 0, Math.PI * 2);
    sctx.fill();

    sctx.fillStyle = p.color;
    sctx.beginPath();
    sctx.arc(cx, cy, Math.min(9, r * 0.4) + 2, 0, Math.PI * 2);
    sctx.fill();
    sctx.strokeStyle = 'rgba(255,255,255,.6)';
    sctx.lineWidth = 1.5;
    sctx.stroke();

    sctx.fillStyle = '#fff';
    sctx.font = '700 11px sans-serif';
    sctx.textAlign = 'center';
    sctx.fillText(`P${p.id}`, cx, cy - r - 4);
    if (p.seats > 0) {
      sctx.fillStyle = '#7ef0c2';
      sctx.font = '700 10px sans-serif';
      sctx.fillText(`★${p.seats > 1 ? p.seats : ''}`, cx, cy + r + 12);
    }
  }

  function draw() {
    const { w, h } = fitCanvas(spectrum, sctx);
    const pad = 14;
    const X = (x) => pad + x * (w - 2 * pad);
    const Y = (y) => pad + y * (h - 2 * pad);
    const eco = cfg.viewMode === 'ecosystem';

    sctx.clearRect(0, 0, w, h);
    sctx.strokeStyle = eco ? '#0f1626' : '#141b2c';
    sctx.lineWidth = 1;
    for (let i = 0; i <= 8; i++) {
      const gx = pad + (i / 8) * (w - 2 * pad);
      const gy = pad + (i / 8) * (h - 2 * pad);
      sctx.beginPath();
      sctx.moveTo(gx, pad);
      sctx.lineTo(gx, h - pad);
      sctx.stroke();
      sctx.beginPath();
      sctx.moveTo(pad, gy);
      sctx.lineTo(w - pad, gy);
      sctx.stroke();
    }

    const t = cfg.framesPerCycle > 0 ? frame / cfg.framesPerCycle : 1;
    const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    const colorOf = new Map(parties.map((p) => [p.id, p.color]));

    // voters — food pellets (ecosystem) or square dots (analyst)
    for (const v of voters) {
      const owned = v.party >= 0 && colorOf.has(v.party);
      if (eco) {
        sctx.fillStyle = owned ? colorOf.get(v.party) : '#4f9d63';
        sctx.globalAlpha = owned ? 0.75 : 0.6;
        sctx.beginPath();
        sctx.arc(X(v.x), Y(v.y), owned ? 1.8 : 2.3, 0, Math.PI * 2);
        sctx.fill();
      } else {
        sctx.fillStyle = owned ? colorOf.get(v.party) : '#33405e';
        sctx.globalAlpha = 0.55;
        sctx.fillRect(X(v.x) - 1, Y(v.y) - 1, 2.4, 2.4);
      }
    }
    sctx.globalAlpha = 1;

    for (const p of parties) {
      p.dx = p.px + (p.x - p.px) * ease;
      p.dy = p.py + (p.y - p.py) * ease;
      if (!p.alive) p.fade = Math.max(0, p.fade - 0.04);
      const r = 8 + Math.sqrt(Math.max(0, p.share)) * 46;
      const cx = X(p.dx);
      const cy = Y(p.dy);

      sctx.globalAlpha = p.fade;
      if (eco) drawCreature(cx, cy, r, p, ease);
      else drawBlob(cx, cy, r, p);
      sctx.globalAlpha = 1;
    }

    parties = parties.filter((p) => p.alive || p.fade > 0);
  }

  function drawCharts() {
    let { w, h } = fitCanvas(enpChart, ectx);
    ectx.clearRect(0, 0, w, h);
    const pad = 30;
    const maxY = Math.max(4, cfg.startParties, ...history.map((d) => d.enp)) * 1.05;
    const px = (i) => pad + (i / Math.max(1, HISTORY_MAX - 1)) * (w - pad - 8);
    const py = (val) => h - pad + 6 - (val / maxY) * (h - pad - 14);

    ectx.font = '10px sans-serif';
    ectx.textAlign = 'right';
    for (let g = 1; g <= maxY; g++) {
      const y = py(g);
      ectx.strokeStyle = g === 2 ? '#2b5c46' : '#1c2438';
      ectx.setLineDash(g === 2 ? [4, 4] : []);
      ectx.beginPath();
      ectx.moveTo(pad, y);
      ectx.lineTo(w - 8, y);
      ectx.stroke();
      ectx.fillStyle = g === 2 ? '#7ef0c2' : '#6b7897';
      ectx.fillText(g === 2 ? '2 (two-party)' : g, pad - 4, y + 3);
    }
    ectx.setLineDash([]);

    if (history.length > 1) {
      ectx.strokeStyle = '#6ea8fe';
      ectx.lineWidth = 2;
      ectx.beginPath();
      history.forEach((d, i) => {
        const XX = px(i);
        const YY = py(d.enp);
        if (i) ectx.lineTo(XX, YY);
        else ectx.moveTo(XX, YY);
      });
      ectx.stroke();
      const last = history[history.length - 1];
      const lx = px(history.length - 1);
      const ly = py(last.enp);
      ectx.fillStyle = '#6ea8fe';
      ectx.beginPath();
      ectx.arc(lx, ly, 3.5, 0, Math.PI * 2);
      ectx.fill();
      ectx.textAlign = 'left';
      ectx.font = '700 12px sans-serif';
      ectx.fillText(last.enp.toFixed(2), Math.min(lx + 6, w - 34), ly - 6);
    }

    ({ w, h } = fitCanvas(shareChart, cctx));
    cctx.clearRect(0, 0, w, h);
    const spx = (i) => pad + (i / Math.max(1, HISTORY_MAX - 1)) * (w - pad - 8);
    const ids = [...new Set(parties.map((p) => p.id))].sort((a, b) => a - b);
    const colorOf = new Map(parties.map((p) => [p.id, p.color]));

    for (const id of ids) {
      cctx.fillStyle = colorOf.get(id) || '#334';
      cctx.globalAlpha = 0.85;
      cctx.beginPath();
      let started = false;
      history.forEach((d, i) => {
        let below = 0;
        for (const oid of ids) {
          if (oid === id) break;
          below += d.shares.get(oid) || 0;
        }
        const top = below + (d.shares.get(id) || 0);
        const XX = spx(i);
        const YY = h - 8 - top * (h - 16);
        if (started) {
          cctx.lineTo(XX, YY);
        } else {
          cctx.moveTo(XX, YY);
          started = true;
        }
      });
      for (let i = history.length - 1; i >= 0; i--) {
        const d = history[i];
        let below = 0;
        for (const oid of ids) {
          if (oid === id) break;
          below += d.shares.get(oid) || 0;
        }
        const XX = spx(i);
        const YY = h - 8 - below * (h - 16);
        cctx.lineTo(XX, YY);
      }
      cctx.closePath();
      cctx.fill();
    }
    cctx.globalAlpha = 1;
    cctx.strokeStyle = 'rgba(255,255,255,.18)';
    cctx.setLineDash([4, 4]);
    const midY = h - 8 - 0.5 * (h - 16);
    cctx.beginPath();
    cctx.moveTo(pad, midY);
    cctx.lineTo(w - 8, midY);
    cctx.stroke();
    cctx.setLineDash([]);
    cctx.fillStyle = '#6b7897';
    cctx.font = '10px sans-serif';
    cctx.textAlign = 'left';
    cctx.fillText('50%', pad + 2, midY - 3);
  }

  // ----------------------------------------------------------- stats report
  function emitStats() {
    const live = alive();
    const last = history[history.length - 1];
    const enp = last ? last.enp : 0;

    let verdict = '—';
    let note = '';
    if (last) {
      verdict = enp <= 2.4 ? 'two' : 'multi';
      if (isSingleWinner(cfg.system)) {
        note =
          enp <= 2.4
            ? 'Single-winner rule → the field has consolidated toward ~2 viable parties, just as Duverger’s Law predicts. Smaller parties lost their base to strategic defection and died.'
            : 'Still consolidating — let it run. Single-winner rules push toward 2. Raise strategic voting to speed up the psychological effect; note that Ranked (IRV) resists consolidation longer than FPTP, but M=1 still pulls it toward two.';
      } else {
        note = `Proportional with M = ${cfg.magnitude} seats → viable parties trend toward roughly M + 1 (~${cfg.magnitude + 1}). The mechanical penalty on small parties is gone, so multi-party pluralism is stable.`;
      }
    }

    onStats?.({
      cycle,
      living: live.length,
      enp,
      verdict,
      note,
      parties: [...parties]
        .sort((a, b) => a.id - b.id)
        .map((p) => ({ id: p.id, color: p.color, share: p.share, alive: p.alive })),
    });
  }

  // -------------------------------------------------------------- lifecycle
  function tick() {
    if (!running) return;
    frame++;
    if (frame >= cfg.framesPerCycle) {
      frame = 0;
      cycle++;
      runElection();
      computeTargets();
      drawCharts();
      emitStats();
    }
    draw();
    rafId = requestAnimationFrame(tick);
  }

  function setRunning(next) {
    running = next;
    onRunningChange?.(next);
  }

  function play() {
    if (running) return;
    setRunning(true);
    tick();
  }

  function pause() {
    setRunning(false);
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
  }

  function toggle() {
    if (running) pause();
    else play();
  }

  function step() {
    pause();
    cycle++;
    runElection();
    computeTargets();
    frame = cfg.framesPerCycle;
    draw();
    drawCharts();
    emitStats();
  }

  function reset() {
    pause();
    cycle = 0;
    frame = 0;
    history = [];
    seedVoters();
    seedParties();
    runElection();
    computeTargets();
    draw();
    drawCharts();
    emitStats();
  }

  function setConfig(partial) {
    Object.assign(cfg, partial);
  }

  function redraw() {
    draw();
    drawCharts();
  }

  function destroy() {
    pause();
  }

  reset();

  return { play, pause, toggle, step, reset, setConfig, redraw, destroy };
}

export { DEFAULT_CONFIG };
